<template>
  <vue-modal-2
    name="ProductMarketplace"
    :headerOptions="{ title: `Request a product` }"
    :noFooter="true"
    modalSize="lg"
  >
    <div class="rounded-lg shadow-sm">
      <!-- Main Content Container -->
      <div class="bg-white dark:bg-slate-800 rounded-lg overflow-hidden">
        <!-- Product Info Section -->
        <div v-if="data.details" class="p-5">
          <!-- Product Header with Image and Info -->
          <div class="flex items-center space-x-4 mb-6">
            <div
              class="h-16 w-16 rounded-full overflow-hidden border-2 border-primary shadow-md"
            >
              <img
                :src="data.picture"
                alt="Product"
                class="h-full w-full object-cover"
              />
            </div>
            <div>
              <h5 class="text-lg font-semibold text-slate-800 dark:text-white">
                {{ data.name }}
              </h5>
              <span class="text-sm text-slate-500 dark:text-slate-400">{{
                data.id_v2
              }}</span>
            </div>
          </div>

          <!-- Warehouse Selection -->
          <div class="mb-6">
            <div class="relative">
              <div
                class="absolute -top-3 left-4 bg-white dark:bg-slate-800 px-2"
              >
                <label
                  class="text-xs font-medium text-slate-600 dark:text-slate-400"
                >
                  Warehouses <span class="text-red-500">*</span>
                </label>
              </div>
              <div
                class="border border-slate-300 dark:border-slate-600 hover:border-primary focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/30 rounded-[6px] p-3 transition-all duration-300"
              >
                <v-select
                  multiple
                  v-model="warehouseSelected"
                  placeholder="Select warehouse..."
                  label="name"
                  :options="warehouses"
                  class="text-slate-700 dark:text-slate-300"
                ></v-select>
              </div>
            </div>
          </div>

          <!-- Tabs Section -->
          <div v-if="warehouseSelected.length > 0" class="mt-4">
            <tabs
              :options="{
                defaultTabHash: `tab_${warehouseSelected[0].country}`,
              }"
              class="warehouse-tabs"
            >
              <tab
                v-for="(pr, key) in warehouseSelected"
                :key="key"
                :id="'tab_' + pr.country"
                :name="pr.name"
              >
                <div
                  class="bg-gradient-to-r from-primary/5 to-blue-300/5 p-5 rounded-[6px] my-4"
                >
                  <div class="grid md:grid-cols-2 gap-6">
                    <div class="relative">
                      <div
                        class="absolute -top-3 left-4 bg-white dark:bg-slate-800 px-2"
                      >
                        <label
                          class="text-xs font-medium text-slate-600 dark:text-slate-400"
                        >
                          {{ $t("Quantity") }}
                          <span class="text-red-500">*</span>
                        </label>
                      </div>
                      <div
                        class="flex items-center border border-slate-300 dark:border-slate-600 hover:border-primary focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/30 rounded-[6px] overflow-hidden transition-all duration-300"
                      >
                        <div
                          class="px-3 py-2 bg-slate-50 dark:bg-slate-700 border-r border-slate-200 dark:border-slate-600"
                        >
                          <Icon
                            icon="heroicons-outline:calculator"
                            class="h-5 w-5 text-primary"
                          />
                        </div>
                        <input
                          type="number"
                          v-model="pr.info.quantity"
                          min="0"
                          class="w-full p-3 bg-white dark:bg-slate-800 focus:outline-none text-slate-700 dark:text-slate-300"
                          placeholder="Enter quantity"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div id="alert-border-1" class="flex items-center p-4 mb-4 text-blue-800 border-t-4 border-blue-300 bg-blue-50 dark:text-blue-400 dark:bg-gray-800 dark:border-blue-800" role="alert">
                  <svg class="shrink-0 w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"/>
                  </svg>
                  <div class="ml-3 text-sm font-medium">
                    Quantité disponible en stock: <b>{{ pr.info.stock }}</b>.
                  </div>
                </div>
                <div  role="alert" class="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3 mt-1">
                  <p  class="font-bold">Votre Profit: <b>Prix de vente - (Shiping Fees + Prix d'Achat)</b></p>
                </div>
              </tab>
            </tabs>
          </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="my-4 flex items-center justify-between px-5">
        <template v-if="currentUser.type === 'Seller'">
          <button
            @click="OpenClosePopupModal('close', 'ProductMarketplace')"
            class="px-4 py-3 rounded-[6px] bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium transition-all duration-300 flex items-center space-x-2"
          >
            <Icon icon="heroicons-outline:x" class="text-lg" />
            <span>Cancel</span>
          </button>

          <button
            @click="EmitActionNewShipp('Marketplace', data, warehouseSelected)"
            class="px-4 py-3 rounded-[6px] bg-gradient-to-r from-primary to-blue-500 hover:from-primary/90 hover:to-blue-600 text-white font-medium transition-all duration-300 flex items-center space-x-2 relative"
            :class="loading ? 'opacity-75 cursor-not-allowed' : ''"
          >
            <loading :active.sync="loading" :is-full-page="fullPage"></loading>
            <Icon icon="heroicons-outline:paper-airplane" class="text-lg" />
            <span>Send Request</span>
          </button>
        </template>

        <template v-else>
          <button
            @click="EmitActionNewShipp('Marketplace', 'rejected')"
            class="px-4 py-3 rounded-[6px] bg-red-100 hover:bg-red-200 text-red-600 font-medium transition-all duration-300 flex items-center space-x-2"
          >
            <Icon icon="heroicons-outline:x-circle" class="text-lg" />
            <span>Reject</span>
          </button>

          <button
            @click="EmitActionNewShipp('Marketplace', 'approved')"
            class="px-4 py-3 rounded-[6px] bg-gradient-to-r from-emerald-500 to-primary hover:from-emerald-600 hover:to-primary/90 text-white font-medium transition-all duration-300 flex items-center space-x-2 relative"
            :class="loading ? 'opacity-75 cursor-not-allowed' : ''"
          >
            <loading :active.sync="loading" :is-full-page="fullPage"></loading>
            <Icon icon="heroicons-outline:check-circle" class="text-lg" />
            <span>Approve</span>
          </button>
        </template>
      </div>
    </div>
  </vue-modal-2>
</template>

<script>
export default {
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object },
    title: String,
    loading: Boolean,
    amount: Number,
    currency: String,
    type: String,
    icon: String,
    fullPage: {
      type: Boolean,
      default() {
        return false;
      },
    },
    statusSous: {
      type: Array,
      default() {
        return [];
      },
    },
    filters: {
      type: Object,
      default() {
        return {};
      },
    },
    paginate: {
      type: Object,
      default() {
        return {};
      },
    },
    data: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      warehouses: [],
      warehouseSelected: [],
    };
  },
  watch: {
    data: function () {
      if (this.data.details) {
        this.warehouses = this.data.details
          .filter((item) => item.warehouse && item.warehouse.is_primary)
          .map((el) => ({
            ...el.warehouse,
            info: { quantity: 0,stock:el.quantity.inStock,price:el.priceMarket },
          }));
      } else {
        this.warehouses = [];
        this.warehouseSelected = [];
      }
    },
  },
  methods: {
    OpenClosePopupModal: async function (val, val2) {
      this.$emit("OpenClosePopupModal", val, val2);
    },
    EmitActionNewShipp: async function (val, val2, val3 = null) {
      this.$emit("EmitActionNewShipp", val, val2, val3);
    },
  },
};
</script>

<style scoped>
.warehouse-tabs .tabs-header {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
}

.warehouse-tabs .tabs-header li {
  padding-left: 1rem;
  padding-right: 1rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  border-radius: 0.5rem;
  background-color: #f1f5f9;
  color: #475569;
  font-weight: 500;
  transition: all 0.3s;
}

@media (prefers-color-scheme: dark) {
  .warehouse-tabs .tabs-header li {
    background-color: #334155;
    color: #cbd5e1;
  }
}

.warehouse-tabs .tabs-header li.active {
  background-color: var(#19a69d);
  color: white;
}

.noselect {
  user-select: none;
  pointer-events: none;
}
</style>
